import React from 'react'
import { Badge, Box, Grid, Text } from '@chakra-ui/react'
import moment from 'moment'
import { formatMoney } from 'lib/numbers'

type BuildiumPostingSummaryProps = {
  amount: number
  date: string
  dateError?: string | null
  dateLabel?: string
  entryDescription: string
  extraRows?: Array<{
    label: string
    value: React.ReactNode
  }>
  isBooksLockError: boolean
  isBooksLockLoading: boolean
  isBuildiumBooksLocked: boolean
  lockDate?: string | null
  typeLabel?: string
}

export default function BuildiumPostingSummary({
  amount,
  date,
  dateError = null,
  dateLabel = 'Date',
  entryDescription,
  extraRows = [],
  isBooksLockError,
  isBooksLockLoading,
  isBuildiumBooksLocked,
  lockDate,
  typeLabel
}: BuildiumPostingSummaryProps) {
  const hasValidDate = Boolean(date && moment(date).isValid())
  const hasPostingDetails = Boolean(typeLabel) && Number.isFinite(amount) && amount > 0
  const formattedPostingDate = hasValidDate ? moment(date).format('M/D/YYYY') : 'Not set'
  const formattedLockDate = lockDate ? moment(lockDate).format('M/D/YYYY') : null
  const postingStatus = (() => {
    if (!hasPostingDetails) {
      return 'Enter details'
    }

    if (!hasValidDate) {
      return 'Enter date'
    }

    if (dateError) {
      return 'Date issue'
    }

    if (isBooksLockLoading) {
      return 'Checking'
    }

    if (isBooksLockError) {
      return 'Verify first'
    }

    if (isBuildiumBooksLocked) {
      return 'Blocked'
    }

    return 'Ready to post'
  })()
  const postingStatusColor = (() => {
    if (postingStatus === 'Ready to post') {
      return 'green'
    }

    if (postingStatus === 'Checking') {
      return 'blue'
    }

    if (postingStatus === 'Blocked' || postingStatus === 'Verify first') {
      return 'red'
    }

    return 'gray'
  })()

  return (
    <Box border='1px solid' borderColor='gray.200' borderRadius='md' bg='gray.50' px={4} py={3}>
      <Box display='flex' justifyContent='space-between' gap={3} alignItems='start' mb={2}>
        <Box>
          <Text fontSize='sm' color='gray.600'>
            Buildium posting
          </Text>
          <Text fontWeight='semibold'>{entryDescription}</Text>
        </Box>
        <Badge colorScheme={postingStatusColor}>{postingStatus}</Badge>
      </Box>
      <Grid
        templateColumns={{ base: '80px minmax(0, 1fr)', sm: '100px minmax(0, 1fr)' }}
        rowGap={1}
        columnGap={3}
        fontSize='sm'
      >
        <Text color='gray.600' whiteSpace={{ base: 'normal', sm: 'nowrap' }}>
          Type
        </Text>
        <Text fontWeight='medium'>{typeLabel || 'No liability type selected'}</Text>
        <Text color='gray.600' whiteSpace={{ base: 'normal', sm: 'nowrap' }}>
          Amount
        </Text>
        <Text fontWeight='medium'>{formatMoney(Number.isFinite(amount) ? amount : 0, 2, '$')}</Text>
        <Text color='gray.600' whiteSpace={{ base: 'normal', sm: 'nowrap' }}>
          {dateLabel}
        </Text>
        <Text fontWeight='medium'>{formattedPostingDate}</Text>
        {extraRows.map((row) => (
          <React.Fragment key={row.label}>
            <Text color='gray.600' whiteSpace={{ base: 'normal', sm: 'nowrap' }}>
              {row.label}
            </Text>
            <Text fontWeight='medium'>{row.value}</Text>
          </React.Fragment>
        ))}
      </Grid>
      {formattedLockDate && (
        <Text mt={2} fontSize='xs' color='gray.600'>
          Buildium books are locked through {formattedLockDate}; postings must be dated after that.
        </Text>
      )}
    </Box>
  )
}
